<template>
  <WdsStack direction-column align-start gap-sm>
    <WdsTag v-for="type in visibleTypes" :key="type" gray sm>{{ $t(type) }}</WdsTag>
  </WdsStack>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { WdsTag, WdsStack } from '@worksome/wds-components'
import { FragmentType, graphql, useFragment } from '@/gql'
import { useTranslation } from '@/composables/translation'

const PaymentRequestTypeColumn = graphql(`
  fragment PaymentRequestTypeColumn on PaymentRequest {
    id
    rateType
    billedAmount
    expensesAmount
    milestones {
      data {
        id
      }
    }
  }
`)

const props = defineProps<{
  row: FragmentType<typeof PaymentRequestTypeColumn>
}>()

const { $t } = useTranslation()
const column = computed(() => useFragment(PaymentRequestTypeColumn, props.row))

const isTimeBased = computed(() => {
  const rateType = column.value.rateType?.toLowerCase()
  return ['hourly', 'daily', 'weekly', 'monthly'].includes(rateType || '')
})

const hasBilledAmount = computed(() => column.value.billedAmount > 0)
const hasMilestone = computed(() => (column.value.milestones?.data?.length ?? 0) > 0)
const hasTime = computed(() => !hasMilestone.value && isTimeBased.value && hasBilledAmount.value)
const hasAmount = computed(() => !hasMilestone.value && !isTimeBased.value && hasBilledAmount.value)
const hasExpenses = computed(() => column.value.expensesAmount > 0)

const visibleTypes = computed(
  () =>
    [
      hasTime.value && 'Time',
      hasAmount.value && 'Amount',
      hasMilestone.value && 'Milestone',
      hasExpenses.value && 'Expenses',
    ].filter(Boolean) as string[],
)
</script>
